// CB:BEGIN project-health-badge
/**
 * ProjectHealthBadge
 *
 * Displays a project's health status as a colored pill with a dot indicator.
 * Used in the admin projects table, war room, and project detail views.
 *
 * Health is computed from project status + updatedAt age.
 */
import React from 'react';

export type ProjectHealth = 'healthy' | 'waiting_homeowner' | 'waiting_contractor' | 'waiting_admin' | 'attention_required';

interface ProjectHealthBadgeProps {
  health: ProjectHealth;
  reason?: string;
  showReason?: boolean;
  size?: 'sm' | 'md';
}

// Status → health mapping (used for client-side computation)
const WAITING_HOMEOWNER_STATUSES = new Set([
  'BIDS_READY',
  'DECIDING',
  'HOMEOWNER_CONTRACT_SIGNING',
  'ESCROW_PENDING',
]);

const WAITING_CONTRACTOR_STATUSES = new Set([
  'BIDS_COLLECTING',
  'BID_SELECTED',
  'SCHEDULED',
]);

const TERMINAL_STATUSES = new Set(['COMPLETED', 'CLOSED', 'CANCELLED', 'DRAFT']);
const STALE_MS = 72 * 60 * 60 * 1000; // 72 hours

/**
 * Compute health from a project's status and updatedAt.
 * Call this client-side when no server-provided health field is available.
 */
export function computeProjectHealth(
  status: string,
  updatedAt?: string | null,
  pendingAdminAction?: boolean,
): { health: ProjectHealth; reason: string } {
  if (status === 'DISPUTED') {
    return { health: 'attention_required', reason: 'Dispute reported' };
  }

  const isStale = updatedAt
    ? Date.now() - new Date(updatedAt).getTime() > STALE_MS
    : false;

  const ACTIVE_PHASES = new Set(['ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'IN_PROGRESS', 'COMPLETED']);
  if (pendingAdminAction && ACTIVE_PHASES.has(status)) {
    return { health: 'waiting_admin', reason: 'Admin action required — milestone review or payment release pending' };
  }

  if (WAITING_HOMEOWNER_STATUSES.has(status)) {
    if (isStale) {
      return { health: 'attention_required', reason: `Waiting on homeowner — no activity for >72h` };
    }
    return { health: 'waiting_homeowner', reason: 'Waiting on homeowner action' };
  }

  if (WAITING_CONTRACTOR_STATUSES.has(status)) {
    if (isStale) {
      return { health: 'attention_required', reason: `Waiting on contractor — no activity for >72h` };
    }
    return { health: 'waiting_contractor', reason: 'Waiting on contractor action' };
  }

  if (TERMINAL_STATUSES.has(status)) {
    return { health: 'healthy', reason: 'Project closed' };
  }

  if (isStale) {
    return { health: 'attention_required', reason: `No activity for >72h (status: ${status})` };
  }

  return { health: 'healthy', reason: 'Project progressing normally' };
}

const CONFIG: Record<ProjectHealth, {
  dot: string;
  bg: string;
  text: string;
  label: string;
}> = {
  healthy: {
    dot: 'bg-green-500',
    bg: 'bg-green-50',
    text: 'text-green-700',
    label: 'Healthy',
  },
  waiting_homeowner: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    label: 'Waiting on Homeowner',
  },
  waiting_contractor: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    label: 'Waiting on Contractor',
  },
  waiting_admin: {
    dot: 'bg-blue-500',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    label: 'Waiting on Admin',
  },
  attention_required: {
    dot: 'bg-red-500',
    bg: 'bg-red-50',
    text: 'text-red-700',
    label: 'Needs Attention',
  },
};

export const ProjectHealthBadge: React.FC<ProjectHealthBadgeProps> = ({
  health,
  reason,
  showReason = false,
  size = 'md',
}) => {
  const c = CONFIG[health];
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-[11px]';

  return (
    <div className="inline-flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold ${textSize} ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
        {c.label}
      </span>
      {showReason && reason && (
        <span className="text-[11px] text-gray-500 pl-1">{reason}</span>
      )}
    </div>
  );
};

export default ProjectHealthBadge;
// CB:END project-health-badge
